/**
 * Guide Me - the TourPlayer overlay.
 *
 * Renders:
 *  - a translucent dim layer over the page
 *  - a virtual cursor (SVG) that animates between targets
 *  - a spotlight ring around the currently-targeted element
 *  - a narration bubble (`aria-live="polite"`)
 *  - a small control bar (back / pause / skip / exit + step counter)
 *
 * Keyboard: Esc exits, ArrowLeft / ArrowRight step, Space toggles pause.
 * Honours `prefers-reduced-motion` (no cursor glide, no easing).
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  type ActionContext,
  type CursorPosition,
  ActionAbortError,
  AssertionError,
  CancellationError,
  HandoffSignal,
  executeStepWithLifecycle,
  runTour as runV2Tour,
} from '@empowernow/guide-me-runtime';
import { applyLocale, pickTourLocaleKey } from '@empowernow/etl-tour';
import { useGuideMeAdapter } from './context';
import { prefetchTourPermissions } from './permissions';
import type { Tour, TourLogEntry } from './types';

const DEFAULT_TIMEOUT_MS = 5000;

/**
 * Pacing defaults — slow enough that the user can comfortably watch and read
 * before the tour advances. Tour authors can override via `pace` or per-step
 * `settleMs`.
 */
const CURSOR_DURATION_DEFAULT = 1400;
const CURSOR_DISTANCE_FACTOR = 2.2;
const CURSOR_DURATION_MIN = 420;
const DEFAULT_SETTLE_MS = 1200;
/**
 * Step types that manage their own dwell time (narrate/wait) OR that are
 * utility steps the user doesn't need to "digest" (navigate/waitFor). These
 * skip the post-step settle pause entirely.
 */
const NO_SETTLE_TYPES = new Set(['narrate', 'wait', 'navigate', 'waitFor', 'chapter']);

/** Step types that may appear in a flat v3 tour. If a tour uses any kind
 *  outside this set (control flow, macro calls, captures, etc.) it goes
 *  through the v2 tree runner. Otherwise the v1 flat loop handles it so
 *  the user gets back/skip affordances. */
const FLAT_V3_STEP_TYPES = new Set([
  'narrate', 'navigate', 'scrollTo', 'move', 'hover', 'click',
  'type', 'press', 'select', 'waitFor', 'wait',
  'expect', 'highlight', 'check', 'uncheck', 'drag', 'multi-spot', 'annotate',
  'chapter',
]);

function isFlatTour(tour: { steps: ReadonlyArray<{ type: string }> }): boolean {
  for (const s of tour.steps) {
    if (!FLAT_V3_STEP_TYPES.has(s.type)) return false;
  }
  return true;
}

/** Maps step types to a short human-readable label and emoji icon. */
const STEP_META: Record<string, { label: string; icon: string }> = {
  narrate:  { label: 'Note',       icon: '💬' },
  navigate: { label: 'Navigating', icon: '📍' },
  scrollTo: { label: 'Scrolling',  icon: '📜' },
  move:     { label: 'Pointing',   icon: '🎯' },
  hover:    { label: 'Hovering',   icon: '🖱️' },
  click:    { label: 'Clicking',   icon: '👆' },
  type:     { label: 'Typing',     icon: '⌨️' },
  press:    { label: 'Key press',  icon: '⌨️' },
  select:   { label: 'Selecting',  icon: '🔽' },
  waitFor:  { label: 'Waiting',    icon: '⏳' },
  wait:     { label: 'Pausing',    icon: '⏳' },
  handoff:  { label: 'Your turn',  icon: '🤝' },
};

export interface TourPlayerProps {
  tour: Tour;
  onClose: () => void;
  /** Called every time narration text changes — used by the tour log sidebar. */
  onNarrate?: (entry: TourLogEntry) => void;
  /**
   * Width (px) of any sidebar panel rendered alongside the player.
   * The overlay, spotlight dim, and narration bubble are confined to the
   * remaining viewport width so the sidebar is never obscured.
   */
  sidebarWidth?: number;
  /**
   * Experience shell locale from {@link LocalizationContext} (`resolvedLocale`).
   * Matched against `tour.locales` keys (e.g. `es-ES` → overlay tag `es`).
   */
  appLocale: string;
}

interface PlayerState {
  status: 'playing' | 'paused' | 'finished' | 'aborted' | 'handoff';
  index: number;
  message: string;
  stepType: string;
  errorMessage?: string;
  /** Current chapter title, set by the most recent `chapter` step. */
  chapter?: string;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export function TourPlayer({ tour, onClose, onNarrate, sidebarWidth = 0, appLocale }: TourPlayerProps) {
  const adapter = useGuideMeAdapter();
  const navigateRef = useRef(adapter.navigate);
  navigateRef.current = adapter.navigate;
  const pathnameRef = useRef(adapter.pathname);
  pathnameRef.current = adapter.pathname;
  const onNarrateRef = useRef(onNarrate);
  onNarrateRef.current = onNarrate;

  const localeKey = useMemo(() => pickTourLocaleKey(appLocale, tour.locales), [appLocale, tour.locales]);
  /** Tour with locale overrides applied — re-derived whenever tour or shell locale changes. */
  const resolvedTour = useMemo(() => applyLocale(tour, localeKey), [tour, localeKey]);
  /**
   * v2 host adapters: top-level identifier values (env / route / plugin
   * / flags / user / tenant) plus the optional `authorize` callback for
   * any `requires permission:` clauses the tour declares. Both come
   * from the adapter context the host wires once at app start. If
   * `authorize` is omitted, permission-gated tours degrade gracefully
   * via the runtime's handoff path.
   */
  const tourTopLevel = adapter.tourTopLevel;
  const authorize = adapter.authorize;

  const reducedMotion = useMemo(() => prefersReducedMotion(), []);
  const [state, setState] = useState<PlayerState>({
    status: 'playing',
    index: 0,
    message: resolvedTour.steps[0]?.narrate ?? resolvedTour.title,
    stepType: 'narrate',
  });
  const [cursor, setCursor] = useState<CursorPosition>({
    x: typeof window !== 'undefined' ? window.innerWidth / 2 : 200,
    y: typeof window !== 'undefined' ? window.innerHeight / 2 : 200,
  });
  const [pulseAt, setPulseAt] = useState<number>(0);
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);
  const [paused, setPaused] = useState<boolean>(false);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const [bubbleSize, setBubbleSize] = useState<{ w: number; h: number }>({ w: 420, h: 120 });

  const cursorRef = useRef<CursorPosition>(cursor);
  cursorRef.current = cursor;

  const cancelledRef = useRef<boolean>(false);
  /** User clicked ◀ / ▶ on the spotlight card (or pressed ArrowLeft / ArrowRight).
   *  Distinct from `cancelledRef` so the catch arm can tell user navigation
   *  apart from a real "tour exit / unmount" cancellation. */
  const userNavRef = useRef<'back' | 'skip' | null>(null);
  const advanceRef = useRef<{ skip: boolean; back: boolean }>({ skip: false, back: false });
  const pausedRef = useRef<boolean>(false);
  pausedRef.current = paused;
  const indexRef = useRef<number>(0);
  indexRef.current = state.index;
  const [runEpoch, setRunEpoch] = useState<number>(0);

  /* ----------------------------- Spotlight redraw on resize/scroll ---------- */
  const spotlightTargetRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    function reposition() {
      const el = spotlightTargetRef.current;
      if (!el || !el.isConnected) {
        setSpotlightRect(null);
        return;
      }
      setSpotlightRect(el.getBoundingClientRect());
    }
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, []);

  const setSpotlight = useCallback((el: HTMLElement | null) => {
    spotlightTargetRef.current = el;
    if (el) setSpotlightRect(el.getBoundingClientRect());
    else setSpotlightRect(null);
  }, []);

  /* ----------------------------- Cursor animation --------------------------- */
  const moveCursor = useCallback(
    (target: CursorPosition, opts?: { durationMs?: number }): Promise<void> =>
      new Promise<void>((resolve) => {
        if (cancelledRef.current) {
          resolve();
          return;
        }
        const start = cursorRef.current;
        const dx = target.x - start.x;
        const dy = target.y - start.y;
        const distance = Math.hypot(dx, dy);
        // Allow tour author override via the optional `cursorMs` pace knob.
        const tourCursorMs = resolvedTour.pace?.cursorMs;
        const baseMs = opts?.durationMs ?? tourCursorMs ?? CURSOR_DURATION_DEFAULT;
        const distanceMs = Math.max(CURSOR_DURATION_MIN, distance * CURSOR_DISTANCE_FACTOR);
        const duration = reducedMotion ? 0 : Math.min(baseMs, distanceMs);
        if (duration === 0) {
          setCursor(target);
          resolve();
          return;
        }
        const startedAt = performance.now();
        let raf = 0;
        const tick = (now: number) => {
          if (cancelledRef.current) {
            resolve();
            return;
          }
          const t = Math.min(1, (now - startedAt) / duration);
          const eased = easeInOutCubic(t);
          setCursor({ x: start.x + dx * eased, y: start.y + dy * eased });
          if (t < 1) {
            raf = requestAnimationFrame(tick);
          } else {
            cancelAnimationFrame(raf);
            resolve();
          }
        };
        raf = requestAnimationFrame(tick);
      }),
    [reducedMotion, resolvedTour.pace?.cursorMs],
  );

  const pulseCursor = useCallback(() => {
    setPulseAt(Date.now());
  }, []);

  const sleep = useCallback(
    (ms: number): Promise<void> =>
      new Promise<void>((resolve) => {
        if (ms <= 0) {
          resolve();
          return;
        }
        const start = Date.now();
        const tick = () => {
          if (cancelledRef.current) {
            resolve();
            return;
          }
          if (pausedRef.current) {
            requestAnimationFrame(tick);
            return;
          }
          if (Date.now() - start >= ms) {
            resolve();
            return;
          }
          requestAnimationFrame(tick);
        };
        tick();
      }),
    [],
  );

  const checkCancelled = useCallback(() => {
    if (cancelledRef.current) throw new CancellationError();
  }, []);

  /* ----------------------------- Action context ----------------------------- */
  const ctxRef = useRef<ActionContext | null>(null);
  ctxRef.current = {
    defaultTimeoutMs: DEFAULT_TIMEOUT_MS,
    demoMode: resolvedTour.demoMode ?? 'dry-run',
    reducedMotion,
    pace: resolvedTour.pace ?? {},
    currentPathname: pathnameRef.current,
    getCursorPos: () => cursorRef.current,
    moveCursor,
    pulseCursor,
    setSpotlight,
    setNarration: (text: string, stepType?: string) => {
      const resolvedType = stepType ?? 'narrate';
      setState((s) => ({ ...s, message: text, stepType: resolvedType }));
      if (onNarrateRef.current) {
        onNarrateRef.current({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          message: text,
          stepType: resolvedType,
          stepIndex: indexRef.current,
        });
      }
    },
    setChapter: (title: string | null) => {
      setState((s) => ({ ...s, chapter: title ?? undefined }));
    },
    navigate: (to: string) => navigateRef.current(to),
    sleep,
    checkCancelled,
  };

  /* ----------------------------- Run loop ----------------------------------- */
  useEffect(() => {
    cancelledRef.current = false;
    let cancelled = false;

    /**
     * Maps an unhandled tour error to terminal player state. Shared
     * between the v1 flat run loop and the v2 tree runner so the
     * user-visible surface stays consistent across grammars.
     */
    const handleTerminalError = (err: unknown, stepIndex: number) => {
      if (err instanceof CancellationError) return true;
      if (err instanceof HandoffSignal) {
        setState((s) => ({ ...s, status: 'handoff', message: err.message, stepType: 'handoff' }));
        onNarrateRef.current?.({
          id: `hoff-${Date.now()}`,
          message: err.message,
          stepType: 'handoff',
          stepIndex,
          terminalStatus: 'handoff',
        });
        return true;
      }
      if (err instanceof ActionAbortError) {
        const abortMsg = 'Couldn’t find that element on this page.';
        setState((s) => ({
          ...s,
          status: 'aborted',
          message: abortMsg,
          stepType: 'narrate',
          errorMessage: err.message,
        }));
        onNarrateRef.current?.({
          id: `abort-${Date.now()}`,
          message: abortMsg,
          stepType: 'narrate',
          stepIndex,
          terminalStatus: 'aborted',
        });
        return true;
      }
      if (err instanceof AssertionError) {
        const failMsg = `Check failed: ${err.message}`;
        setState((s) => ({
          ...s,
          status: 'aborted',
          message: failMsg,
          stepType: 'expect',
          errorMessage: err.message,
        }));
        onNarrateRef.current?.({
          id: `assert-${Date.now()}`,
          message: failMsg,
          stepType: 'expect',
          stepIndex,
          terminalStatus: 'aborted',
        });
        return true;
      }
      setState((s) => ({
        ...s,
        status: 'aborted',
        message: 'Something went wrong while running the tour.',
        errorMessage: (err as Error)?.message ?? String(err),
      }));
      return true;
    };

    /**
     * v2 tours go through the tree runner so block constructs (`if`,
     * `forEach`, `try/catch/finally`) work. The v1 flat loop below
     * keeps its skip/back affordances; the v2 runner doesn't yet wire
     * skip/back (Phase 3 scope) so authors model expected failures via
     * `try/catch`.
     */
    const runV2 = async () => {
      const ctx = ctxRef.current!;
      try {
        // Prefetch every `requires permission:` clause against the host
        // PDP so the runner sees a synchronous lookup. Tours without
        // permission clauses skip the prefetch entirely.
        let checkPermission: ((action: string, resource?: string) => boolean) | undefined;
        const hasPermissionRequires =
          (resolvedTour.requires ?? []).some((r) => r.kind === 'permission');
        if (hasPermissionRequires) {
          if (!authorize) {
            // The tour declares `requires permission:` but the host
            // adapter didn't surface an `authorize` callback. Fail
            // closed via the gating handoff path so the user sees a
            // clear "this tour is gated" message rather than a render
            // crash.
            throw new HandoffSignal(
              'This tour requires a permission check, but the host did not provide an authorize() callback.',
            );
          }
          checkPermission = await prefetchTourPermissions(authorize, resolvedTour);
          if (cancelledRef.current) return;
        }
        await runV2Tour(ctx, resolvedTour, {
          topLevel: tourTopLevel,
          checkPermission,
        });
        if (cancelledRef.current) return;
        const msg = 'You reached the end of the tour.';
        setState((s) => ({ ...s, status: 'finished', message: msg, stepType: 'narrate' }));
        onNarrateRef.current?.({
          id: `fin-${Date.now()}`,
          message: msg,
          stepType: 'narrate',
          stepIndex: 0,
          terminalStatus: 'finished',
        });
      } catch (err) {
        handleTerminalError(err, 0);
      }
    };

    const runLoop = async () => {
      // v3 tours that use block constructs (if/forEach/try/let/etc.) need
      // the tree runner. Flat v3 tours — only action/visual/chapter steps —
      // route through the v1 loop so the user gets working ◀/▶ controls.
      if (resolvedTour.etlVersion === 3 && !isFlatTour(resolvedTour)) {
        await runV2();
        return;
      }
      while (!cancelled && !cancelledRef.current) {
        const i = indexRef.current;
        if (i >= resolvedTour.steps.length) {
          const msg = 'You reached the end of the tour.';
          setState((s) => ({ ...s, status: 'finished', message: msg, stepType: 'narrate' }));
          onNarrateRef.current?.({
            id: `fin-${Date.now()}`,
            message: msg,
            stepType: 'narrate',
            stepIndex: i,
            terminalStatus: 'finished',
          });
          return;
        }

        // Reset per-step controls.
        advanceRef.current = { skip: false, back: false };
        const ctx = ctxRef.current!;
        const step = resolvedTour.steps[i];

        try {
          await executeStepWithLifecycle(ctx, step);
          if (cancelledRef.current) return;

          // Settle pause - lets the user read the bubble and see the result of
          // the action before the cursor moves to the next target. Skipped for
          // narrate/wait steps which already control their own dwell time, and
          // honoured at zero when the author explicitly sets settleMs: 0.
          if (!NO_SETTLE_TYPES.has(step.type)) {
            const settle =
              step.settleMs !== undefined
                ? step.settleMs
                : resolvedTour.pace?.settleMs ?? DEFAULT_SETTLE_MS;
            if (settle > 0) await ctx.sleep(settle);
            if (cancelledRef.current) return;
          }

          if (advanceRef.current.back) {
            indexRef.current = Math.max(0, i - 1);
          } else {
            indexRef.current = i + 1;
          }
          setState((s) => ({ ...s, index: indexRef.current }));
        } catch (err) {
          if (err instanceof CancellationError) {
            // Distinguish user navigation (◀/▶ during a step) from a true
            // cancellation (handleClose / effect cleanup / unmount). On
            // user nav we adjust the index and continue; only true cancel
            // ends the loop.
            const nav = userNavRef.current;
            if (nav === 'back' || nav === 'skip') {
              userNavRef.current = null;
              cancelledRef.current = false;
              let next = nav === 'back'
                ? Math.max(0, i - 1)
                : Math.min(resolvedTour.steps.length, i + 1);
              // Chapter markers are metadata, not content. Step past them
              // so back/forward lands on the previous/next real content step.
              while (
                next > 0 &&
                next < resolvedTour.steps.length &&
                resolvedTour.steps[next].type === 'chapter'
              ) {
                next = nav === 'back' ? next - 1 : next + 1;
              }
              indexRef.current = next;
              setState((s) => ({ ...s, index: next, status: 'playing', errorMessage: undefined }));
              continue;
            }
            return;
          }
          if (err instanceof HandoffSignal) {
            setState((s) => ({ ...s, status: 'handoff', message: err.message, stepType: 'handoff' }));
            onNarrateRef.current?.({
              id: `hoff-${Date.now()}`,
              message: err.message,
              stepType: 'handoff',
              stepIndex: i,
              terminalStatus: 'handoff',
            });
            return;
          }
          if (err instanceof ActionAbortError) {
            if (advanceRef.current.skip) {
              indexRef.current = i + 1;
              setState((s) => ({ ...s, index: indexRef.current }));
              continue;
            }
            // Tour opt-in: `on-error skip` in the header tells the runtime
            // to narrate the miss and continue instead of aborting. Used
            // for onboarding tours that walk transient UI.
            if (resolvedTour.onError === 'skip') {
              indexRef.current = i + 1;
              const skipMsg = 'Skipped \u2014 couldn\u2019t find that element.';
              setState((s) => ({ ...s, index: indexRef.current, message: skipMsg, stepType: 'narrate' }));
              onNarrateRef.current?.({
                id: `skip-${Date.now()}`,
                message: skipMsg,
                stepType: 'narrate',
                stepIndex: i,
              });
              continue;
            }
            const abortMsg = 'Couldn\u2019t find that element on this page.';
            setState((s) => ({
              ...s,
              status: 'aborted',
              message: abortMsg,
              stepType: 'narrate',
              errorMessage: err.message,
            }));
            onNarrateRef.current?.({
              id: `abort-${Date.now()}`,
              message: abortMsg,
              stepType: 'narrate',
              stepIndex: i,
              terminalStatus: 'aborted',
            });
            return;
          }
          if (err instanceof AssertionError) {
            // Hard-fail assertions surface as "Check failed: \u2026" with the
            // same skip-or-stop affordances ActionAbortError gets. Soft
            // failures never reach this catch arm \u2014 they're narrated
            // inline by runExpect and never throw.
            if (advanceRef.current.skip) {
              indexRef.current = i + 1;
              setState((s) => ({ ...s, index: indexRef.current }));
              continue;
            }
            const failMsg = `Check failed: ${err.message}`;
            setState((s) => ({
              ...s,
              status: 'aborted',
              message: failMsg,
              stepType: 'expect',
              errorMessage: err.message,
            }));
            onNarrateRef.current?.({
              id: `assert-${Date.now()}`,
              message: failMsg,
              stepType: 'expect',
              stepIndex: i,
              terminalStatus: 'aborted',
            });
            return;
          }
          setState((s) => ({
            ...s,
            status: 'aborted',
            message: 'Something went wrong while running the tour.',
            errorMessage: (err as Error)?.message ?? String(err),
          }));
          return;
        }
      }
    };

    runLoop();

    return () => {
      cancelled = true;
      cancelledRef.current = true;
    };
    // run once per tour instance and whenever the user manually steps after a handoff/abort.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedTour, runEpoch]);

  /* ----------------------------- Controls ----------------------------------- */
  const handleClose = useCallback(() => {
    cancelledRef.current = true;
    setState((s) => ({ ...s, status: 'aborted', message: '' }));
    onClose();
  }, [onClose]);

  // ◀/▶ during a step: signal the current step to cancel, then let the
  // run loop's CancellationError handler bump the index and continue.
  // The terminal states (handoff / aborted / finished) restart the loop
  // via setRunEpoch — those paths still need the epoch bump so the
  // effect re-runs from the new index.
  const handleSkip = useCallback(() => {
    setState((s) => {
      const isTerminal = s.status === 'handoff' || s.status === 'aborted' || s.status === 'finished';
      if (isTerminal) {
        indexRef.current = Math.min(resolvedTour.steps.length, indexRef.current + 1);
        cancelledRef.current = false;
        setRunEpoch((n) => n + 1);
        return { ...s, index: indexRef.current, status: 'playing', errorMessage: undefined };
      }
      userNavRef.current = 'skip';
      cancelledRef.current = true;
      return s;
    });
  }, [resolvedTour.steps.length]);

  const handleBack = useCallback(() => {
    setState((s) => {
      const isTerminal = s.status === 'handoff' || s.status === 'aborted' || s.status === 'finished';
      if (isTerminal) {
        indexRef.current = Math.max(0, indexRef.current - 1);
        cancelledRef.current = false;
        setRunEpoch((n) => n + 1);
        return { ...s, index: indexRef.current, status: 'playing', errorMessage: undefined };
      }
      userNavRef.current = 'back';
      cancelledRef.current = true;
      return s;
    });
  }, []);

  const handlePause = useCallback(() => {
    setPaused((p) => !p);
  }, []);

  /* ----------------------------- Keyboard ---------------------------------- */
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      // Ignore synthetic events dispatched by the tour itself (e.g. a
      // `press Escape` step) — only respond to genuine user input.
      // Without this guard the runner's own Escape closes the tour
      // before the launcher / mega-menu's own handler gets the event.
      if (!ev.isTrusted) return;
      if (ev.key === 'Escape') {
        ev.preventDefault();
        handleClose();
      } else if (ev.key === 'ArrowRight') {
        ev.preventDefault();
        handleSkip();
      } else if (ev.key === 'ArrowLeft') {
        ev.preventDefault();
        handleBack();
      } else if (ev.key === ' ') {
        ev.preventDefault();
        handlePause();
      }
    }
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true } as any);
  }, [handleClose, handleSkip, handleBack, handlePause]);

  /* ----------------------------- Bubble positioning ------------------------ */
  // Measure the bubble so the smart placement logic can keep it on-screen and
  // adjacent to the spotlighted element. ResizeObserver covers content reflow
  // (longer narrations); window resize covers viewport changes.
  useLayoutEffect(() => {
    const el = bubbleRef.current;
    if (!el || typeof window === 'undefined') return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setBubbleSize((prev) => {
        if (Math.abs(prev.w - r.width) < 1 && Math.abs(prev.h - r.height) < 1) return prev;
        return { w: r.width, h: r.height };
      });
    };
    measure();
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
    // re-measure when the message changes since text length affects height.
  }, [state.message, state.errorMessage]);

  /* ----------------------------- Render ------------------------------------ */
  if (typeof document === 'undefined') return null;

  const totalSteps = resolvedTour.steps.length;
  const stepNumber = Math.min(state.index + 1, totalSteps);
  const isFinished = state.status === 'finished';
  const isHandoff = state.status === 'handoff';
  const isAborted = state.status === 'aborted';
  const showActionable = !isFinished && !isAborted;

  const spotlightStyle: React.CSSProperties | null = spotlightRect
    ? {
        position: 'fixed',
        top: spotlightRect.top - 6,
        left: spotlightRect.left - 6,
        width: spotlightRect.width + 12,
        height: spotlightRect.height + 12,
        borderRadius: 10,
        boxShadow:
          '0 0 0 9999px rgba(8, 12, 24, 0.55), 0 0 0 2px rgba(255,255,255,0.95), 0 0 0 6px rgba(99,102,241,0.55), 0 0 30px rgba(99,102,241,0.5)',
        pointerEvents: 'none',
        transition: reducedMotion ? 'none' : 'top 200ms ease-out, left 200ms ease-out, width 200ms, height 200ms',
        zIndex: 999998,
      }
    : null;

  const overlay = (
    <div
      data-testid="guide-me-player"
      role="dialog"
      aria-modal="false"
      aria-label={`Guide Me: ${resolvedTour.title}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        /* Leave the sidebar area uncovered so it stays interactive. */
        right: sidebarWidth,
        zIndex: 999990,
        pointerEvents: 'none',
      }}
    >
      {/* Dim layer when there is no spotlight (so first/last narration steps still focus attention). */}
      {!spotlightStyle && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(8, 12, 24, 0.45)',
            transition: 'opacity 200ms',
          }}
        />
      )}

      {/* Spotlight ring */}
      {spotlightStyle && <div aria-hidden="true" style={spotlightStyle} />}

      {/* Virtual cursor - pointer-events: none so it never intercepts the user's real mouse. */}
      <svg
        aria-hidden="true"
        width="32"
        height="32"
        viewBox="0 0 32 32"
        style={{
          position: 'fixed',
          left: cursor.x - 6,
          top: cursor.y - 4,
          pointerEvents: 'none',
          zIndex: 999999,
          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.45))',
          transition: reducedMotion ? 'none' : undefined,
        }}
      >
        <path
          d="M5 4 L5 26 L11 20 L15 28 L19 26 L15 18 L23 18 Z"
          fill="white"
          stroke="black"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        {/* Click ripple - fades quickly */}
        {pulseAt > 0 && (
          <circle
            key={pulseAt}
            cx="6"
            cy="5"
            r="2"
            fill="rgba(99,102,241,0.4)"
            stroke="rgba(99,102,241,0.9)"
            strokeWidth="1"
          >
            <animate attributeName="r" values="2;18" dur="450ms" fill="freeze" />
            <animate attributeName="opacity" values="1;0" dur="450ms" fill="freeze" />
          </circle>
        )}
      </svg>

      {/* Narration bubble — positioned adjacent to the spotlight when one exists,
          falling back to bottom-center for narrate-only steps. */}
      <div
        ref={bubbleRef}
        style={{
          ...computeBubblePosition(spotlightRect, bubbleSize, sidebarWidth, collectBubbleObstacles(spotlightRect)),
          position: 'fixed',
          width: 'min(460px, calc(100vw - 32px))',
          background:
            'linear-gradient(135deg, #221655 0%, #1a1244 55%, #14102e 100%)',
          color: 'white',
          borderRadius: 16,
          boxShadow:
            '0 24px 70px rgba(124, 58, 237, 0.45), 0 0 0 1px rgba(196, 181, 253, 0.35), 0 0 36px rgba(139, 92, 246, 0.32)',
          border: '1px solid rgba(167, 139, 250, 0.6)',
          pointerEvents: 'auto',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
          zIndex: 1000000,
          transition: reducedMotion ? 'none' : 'top 220ms ease-out, left 220ms ease-out',
          overflow: 'hidden',
        }}
        data-testid="guide-me-narration"
      >
        {/* Signature accent strip — ties the bubble visually to the Guide Me orb halo */}
        <div
          aria-hidden="true"
          style={{
            height: 3,
            background:
              'linear-gradient(90deg, #f59e0b 0%, #ef4444 22%, #ec4899 44%, #8b5cf6 66%, #3b82f6 88%, #10b981 100%)',
          }}
        />

        {/* Header bar — icon, action label, tour name, step counter */}
        {(() => {
          const meta = STEP_META[state.stepType] ?? STEP_META.narrate;
          const pct = totalSteps > 1 ? ((stepNumber - 1) / (totalSteps - 1)) * 100 : 100;
          return (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 16px 8px',
                borderBottom: '1px solid rgba(167, 139, 250, 0.28)',
              }}>
                <span style={{ fontSize: 18, lineHeight: 1 }}>{meta.icon}</span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'rgba(221, 214, 254, 0.98)',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 240,
                  }}
                  title={state.chapter ?? resolvedTour.title}
                >
                  {state.chapter ?? resolvedTour.title}
                </span>
                <span style={{ fontSize: 11, color: 'rgba(196, 181, 253, 0.7)' }}>·</span>
                <span style={{ fontSize: 12, color: 'rgba(196, 181, 253, 0.85)', whiteSpace: 'nowrap' }}>
                  {meta.label}
                </span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 11, color: 'rgba(196, 181, 253, 0.85)', whiteSpace: 'nowrap' }}>
                  {stepNumber} / {totalSteps}
                </span>
              </div>
              {/* Progress bar */}
              <div style={{ height: 3, background: 'rgba(167, 139, 250, 0.18)' }}>
                <div style={{
                  height: '100%',
                  width: `${pct}%`,
                  background: 'linear-gradient(90deg, #8b5cf6, #ec4899, #f59e0b)',
                  transition: reducedMotion ? 'none' : 'width 400ms ease-out',
                  borderRadius: '0 2px 2px 0',
                }} />
              </div>
            </>
          );
        })()}

        {/* Body — narration text */}
        <div style={{ padding: '12px 16px 10px' }}>
          <div aria-live="polite" style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.55, color: 'rgba(241,245,249,0.97)' }}>
            {state.message}
          </div>
          {state.errorMessage && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'rgba(252,165,165,0.95)', background: 'rgba(252,165,165,0.08)', borderRadius: 6, padding: '6px 8px' }}>
              ⚠️ {state.errorMessage}
            </div>
          )}
          {(isHandoff || isAborted || isFinished) && (
            <div style={{ marginTop: 8, fontSize: 13, color: 'rgba(203,213,225,0.85)', display: 'flex', alignItems: 'center', gap: 6 }}>
              {/* `color: inherit` keeps the label spans on the parent's light
                  text; an inline style beats the shell's non-!important
                  `[data-theme] span { color: var(--main-text) }` rule that
                  would otherwise darken them on this dark bubble. */}
              {isHandoff && <><span>🤝</span><span style={{ color: 'inherit' }}>You&rsquo;re in control — click when ready.</span></>}
              {isFinished && <><span>✅</span><span style={{ color: 'inherit' }}>Tour complete!</span></>}
              {isAborted && <><span>⚠️</span><span style={{ color: 'inherit' }}>Element not found — skip or exit.</span></>}
            </div>
          )}
        </div>

        {/* Controls footer */}
        <div style={{
          display: 'flex',
          gap: 6,
          alignItems: 'center',
          padding: '8px 16px 12px',
          borderTop: '1px solid rgba(167, 139, 250, 0.22)',
          flexWrap: 'wrap',
        }}>
          {showActionable && (
            <>
              <ControlButton onClick={handleBack} aria-label="Previous step" disabled={state.index === 0}>◀</ControlButton>
              <ControlButton onClick={handlePause} aria-label={paused ? 'Resume' : 'Pause'}>
                {paused ? '▶' : '❚❚'}
              </ControlButton>
              <ControlButton onClick={handleSkip} aria-label="Next step">▶</ControlButton>
            </>
          )}
          {/* Ask the guide (Phase 2) — temporarily hidden */}
          {resolvedTour.guidePath && (
            <ControlAnchor
              href={adapter.buildGuideUrl(resolvedTour.guidePath) ?? '#'}
              aria-label="Open prose user guide in a new tab"
              data-testid="guide-me-open-guide"
              title="Open the written guide for this task in a new tab"
            >
              <span aria-hidden="true" style={{ fontSize: 13, color: 'inherit' }}>📖 Guide</span>
            </ControlAnchor>
          )}
          <span style={{ flex: 1 }} />
          <ControlButton onClick={handleClose} aria-label="Exit guide" variant="primary" data-testid="guide-me-exit">
            ✕ Exit
          </ControlButton>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}

/* -------------------------------------------------------------------------- */
/* Bubble positioning                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Place the narration bubble adjacent to the spotlighted element so the help
 * text reads in context. Tries below → above → right → left, picking the first
 * placement that fits in the viewport with margin. Falls back to bottom-center
 * when no spotlight is set (narrate-only steps). Returns `top`/`left` only;
 * width/positioning kind are owned by the caller.
 */
/**
 * Place the narration bubble adjacent to the spotlighted element.
 * `sidebarWidth` carves out the right-hand side occupied by TourLogPanel so
 * the bubble is never placed underneath the sidebar.
 */
/** Selectors for open menus / dropdowns / dialogs whose visible body should
 *  not be covered by the narration bubble. */
/** Inner panels/dropdowns only — never full-screen backdrops/scrims, since
 *  those would cover every candidate placement and force the bubble into the
 *  last-ditch corner. */
const OBSTACLE_SELECTORS = [
  '.mega-menu-panel',
  '.user-menu-dropdown',
  '.launcher-panel',
  '[role="menu"]',
  '[role="dialog"]',
].join(', ');

/** Discard rects that span (or nearly span) the entire viewport — those are
 *  almost certainly modal backdrops/scrims and would otherwise eliminate
 *  every available bubble placement. */
function isViewportSpan(rect: DOMRect): boolean {
  if (typeof window === 'undefined') return false;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return rect.width >= vw * 0.95 && rect.height >= vh * 0.9;
}

/** Collect rects of open menus / dropdowns / dialogs that the bubble must not overlap.
 *  Rects that contain the spotlight are KEPT — we still need to route the bubble
 *  outside the containing modal so the bubble doesn't cover the modal's own
 *  content (e.g. launcher results below the search input). The spotlight itself
 *  is whitelisted via per-rect candidate generation in computeBubblePosition. */
function collectBubbleObstacles(_spotlight: DOMRect | null): DOMRect[] {
  if (typeof document === 'undefined') return [];
  const out: DOMRect[] = [];
  const nodes = document.querySelectorAll<HTMLElement>(OBSTACLE_SELECTORS);
  nodes.forEach((el) => {
    // Skip the Guide Me player's own overlay — its role="dialog" wrapper
    // would otherwise cover the entire content area and force the bubble
    // into the last-ditch fallback placement.
    if (el.closest('[data-testid="guide-me-player"]')) return;
    const rect = el.getBoundingClientRect();
    if (rect.width < 4 || rect.height < 4) return;
    if (isViewportSpan(rect)) return;
    out.push(rect);
  });
  return out;
}

function rectsOverlap(a: { top: number; left: number; w: number; h: number }, b: DOMRect): boolean {
  return !(
    a.left + a.w <= b.left ||
    a.left >= b.right ||
    a.top + a.h <= b.top ||
    a.top >= b.bottom
  );
}

function computeBubblePosition(
  spotlight: DOMRect | null,
  bubble: { w: number; h: number },
  sidebarWidth = 0,
  obstacles: DOMRect[] = [],
): { top: number; left: number } {
  const fullVw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 720;
  /** Effective viewport width — excludes the sidebar column. */
  const vw = fullVw - sidebarWidth;
  const margin = 12;
  const gap = 18;

  const clampLeft = (left: number): number =>
    Math.max(margin, Math.min(left, vw - bubble.w - margin));
  const clampTop = (top: number): number =>
    Math.max(margin, Math.min(top, vh - bubble.h - margin));

  const clearsObstacles = (top: number, left: number): boolean => {
    if (obstacles.length === 0) return true;
    const box = { top, left, w: bubble.w, h: bubble.h };
    return !obstacles.some((o) => rectsOverlap(box, o));
  };

  if (!spotlight) {
    return { top: vh - bubble.h - 28, left: clampLeft((vw - bubble.w) / 2) };
  }

  const centerX = spotlight.left + spotlight.width / 2;
  const centerY = spotlight.top + spotlight.height / 2;

  type Candidate = { top: number; left: number };
  const candidates: Candidate[] = [];

  // Below the spotlight (preferred).
  const belowTop = spotlight.bottom + gap;
  if (belowTop + bubble.h + margin <= vh) {
    candidates.push({ top: belowTop, left: clampLeft(centerX - bubble.w / 2) });
  }
  // Above the spotlight.
  const aboveTop = spotlight.top - bubble.h - gap;
  if (aboveTop >= margin) {
    candidates.push({ top: aboveTop, left: clampLeft(centerX - bubble.w / 2) });
  }
  // Right of the spotlight.
  const rightLeft = spotlight.right + gap;
  if (rightLeft + bubble.w + margin <= vw) {
    candidates.push({ top: clampTop(centerY - bubble.h / 2), left: rightLeft });
  }
  // Left of the spotlight.
  const leftLeft = spotlight.left - bubble.w - gap;
  if (leftLeft >= margin) {
    candidates.push({ top: clampTop(centerY - bubble.h / 2), left: leftLeft });
  }

  // If obstacles exist, also route around them: try sides of each obstacle.
  for (const o of obstacles) {
    // Right of the obstacle.
    const oRight = o.right + gap;
    if (oRight + bubble.w + margin <= vw) {
      candidates.push({ top: clampTop(centerY - bubble.h / 2), left: oRight });
    }
    // Left of the obstacle.
    const oLeft = o.left - bubble.w - gap;
    if (oLeft >= margin) {
      candidates.push({ top: clampTop(centerY - bubble.h / 2), left: oLeft });
    }
    // Below the obstacle.
    const oBelow = o.bottom + gap;
    if (oBelow + bubble.h + margin <= vh) {
      candidates.push({ top: oBelow, left: clampLeft(centerX - bubble.w / 2) });
    }
  }

  // First non-overlapping candidate wins.
  for (const c of candidates) {
    if (clearsObstacles(c.top, c.left)) return c;
  }

  // Last-ditch: bottom-center within the effective viewport width.
  return { top: vh - bubble.h - 16, left: clampLeft((vw - bubble.w) / 2) };
}

/* -------------------------------------------------------------------------- */
/* ControlButton - small inline component to keep TourPlayer self-contained.   */
/* -------------------------------------------------------------------------- */

function ControlButton({
  children,
  onClick,
  variant = 'default',
  disabled = false,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'default' | 'primary' }) {
  const isPrimary = variant === 'primary';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      {...rest}
      style={{
        background: isPrimary
          ? 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)'
          : 'rgba(76, 49, 156, 0.65)',
        color: 'white',
        border: `1px solid ${isPrimary ? 'rgba(196, 181, 253, 0.6)' : 'rgba(167, 139, 250, 0.45)'}`,
        borderRadius: 8,
        padding: '6px 10px',
        fontSize: 13,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        minWidth: 32,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 150ms',
        boxShadow: isPrimary ? '0 6px 18px rgba(139, 92, 246, 0.45)' : undefined,
      }}
    >
      {children}
    </button>
  );
}

/**
 * Anchor styled as a control button. Used for the "open guide in new tab"
 * control so the browser handles target=_blank natively (no JS popup).
 */
function ControlAnchor({
  children,
  href,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      {...rest}
      style={{
        background: 'rgba(76, 49, 156, 0.65)',
        color: 'white',
        border: '1px solid rgba(167, 139, 250, 0.45)',
        borderRadius: 8,
        padding: '6px 10px',
        fontSize: 13,
        cursor: 'pointer',
        minWidth: 32,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none',
        transition: 'background 150ms',
      }}
    >
      {children}
    </a>
  );
}
